import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  FormControlLabel,
  Switch,
  Alert,
  useTheme,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import {
  DistributionGroup,
  CreateDistributionGroupInput,
  UpdateDistributionGroupInput
} from '@/types/distributionGroupTypes';
import { AttioTextField } from '@/components/AttioForm';

interface DistributionGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateDistributionGroupInput | UpdateDistributionGroupInput) => Promise<void>;
  editingGroup?: DistributionGroup | null;
}

interface FormData {
  name: string;
  description: string;
  emailAddresses: string[];
  isActive: boolean;
}

const defaultFormData: FormData = {
  name: '',
  description: '',
  emailAddresses: [],
  isActive: true
};

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const DistributionGroupDialog: React.FC<DistributionGroupDialogProps> = ({
  open,
  onClose,
  onSubmit,
  editingGroup
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const isEditing = !!editingGroup;

  // Reset form when dialog opens/closes or editing group changes
  useEffect(() => {
    if (open) {
      if (editingGroup) {
        setFormData({
          name: editingGroup.name,
          description: editingGroup.description,
          emailAddresses: [...editingGroup.emailAddresses],
          isActive: editingGroup.isActive
        });
      } else {
        setFormData(defaultFormData);
      }
      setNewEmail('');
      setEmailError('');
      setFormErrors({});
    }
  }, [open, editingGroup]);

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      errors.name = 'Group name is required';
    }

    if (formData.emailAddresses.length === 0) {
      errors.emails = 'At least one email address is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddEmail = () => {
    const email = newEmail.trim().toLowerCase();
    
    if (!email) {
      setEmailError('Please enter an email address');
      return;
    }

    if (!EMAIL_REGEX.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (formData.emailAddresses.includes(email)) {
      setEmailError('This email address is already added');
      return;
    }

    setFormData(prev => ({
      ...prev,
      emailAddresses: [...prev.emailAddresses, email]
    }));
    setNewEmail('');
    setEmailError('');
    
    // Clear email errors if we now have emails
    if (formErrors.emails) {
      setFormErrors(prev => ({ ...prev, emails: '' }));
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      emailAddresses: prev.emailAddresses.filter(email => email !== emailToRemove)
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditing && editingGroup) {
        await onSubmit({
          id: editingGroup.id,
          name: formData.name.trim(),
          description: formData.description.trim(),
          emailAddresses: formData.emailAddresses,
          isActive: formData.isActive
        });
      } else {
        await onSubmit({
          name: formData.name.trim(),
          description: formData.description.trim(),
          emailAddresses: formData.emailAddresses,
          isActive: formData.isActive
        });
      }
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 8px 32px rgba(0, 0, 0, 0.6)' 
            : '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Typography 
          sx={{ 
            fontSize: '18px',
            fontWeight: 600,
            color: theme.palette.text.primary
          }}
        >
          {isEditing ? 'Edit Distribution Group' : 'Create Distribution Group'}
        </Typography>
        <IconButton 
          onClick={handleClose} 
          disabled={isSubmitting}
          sx={{
            borderRadius: '6px',
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          {/* Basic Information */}
          <Box>
            <Typography 
              sx={{ 
                fontSize: '11px',
                fontWeight: 600,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                mb: 2.5,
                pt: 1.5
              }}
            >
              Basic Information
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <AttioTextField
                label="Group Name"
                value={formData.name}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, name: e.target.value }));
                  if (formErrors.name) {
                    setFormErrors(prev => ({ ...prev, name: '' }));
                  }
                }}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
                placeholder="e.g., Legal Department"
              />

              <AttioTextField
                label="Description"
                value={formData.description}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, description: e.target.value }));
                  if (formErrors.description) {
                    setFormErrors(prev => ({ ...prev, description: '' }));
                  }
                }}
                error={!!formErrors.description}
                helperText={formErrors.description}
                placeholder="Describe who receives summaries from this group"
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1
                }}
              >
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: theme.palette.text.primary }}>
                  Active (group can receive summaries)
                </Typography>
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              </Box>
            </Box>
          </Box>

          {/* Email Addresses */}
          <Box>
            <Typography 
              sx={{ 
                fontSize: '11px',
                fontWeight: 600,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                mb: 1.5,
                pt: 1.5
              }}
            >
              Email Recipients
            </Typography>

            {/* Info Alert - shown when no emails */}
            {formData.emailAddresses.length === 0 && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 2.5,
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.04)'
                }}
              >
                Add email addresses for people who should receive summaries from this distribution group.
              </Alert>
            )}

            {/* Add Email Input */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, alignItems: 'flex-end' }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    mb: 0.75
                  }}
                >
                  Email Address
                </Typography>
                <TextField
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    if (emailError) setEmailError('');
                  }}
                  onKeyPress={handleKeyPress}
                  error={!!emailError}
                  helperText={emailError}
                  placeholder="user@company.com"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '14px'
                    },
                    '& .MuiInputBase-input': {
                      py: 1.25,
                      px: 1.75
                    }
                  }}
                />
              </Box>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddEmail}
                disabled={!newEmail.trim()}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '14px',
                  px: 2.5,
                  py: 1.25,
                  minWidth: 100,
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6',
                    borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
                  }
                }}
              >
                Add
              </Button>
            </Box>

            {/* Email List */}
            {formData.emailAddresses.length > 0 && (
              <Box>
                <Typography 
                  sx={{ 
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                    mb: 1.5,
                    fontWeight: 500
                  }}
                >
                  {formData.emailAddresses.length} recipient{formData.emailAddresses.length !== 1 ? 's' : ''}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.emailAddresses.map((email, index) => (
                    <Chip
                      key={index}
                      label={email}
                      onDelete={() => handleRemoveEmail(email)}
                      deleteIcon={<DeleteIcon />}
                      sx={{
                        height: '28px',
                        fontSize: '13px',
                        borderRadius: '6px',
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6',
                        border: `1px solid ${theme.palette.divider}`,
                        '& .MuiChip-deleteIcon': {
                          color: theme.palette.text.secondary,
                          fontSize: '16px',
                          '&:hover': {
                            color: 'error.main'
                          }
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {formErrors.emails && (
              <Alert 
                severity="error" 
                sx={{ 
                  mt: 1,
                  borderRadius: '8px'
                }}
              >
                {formErrors.emails}
              </Alert>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          px: 3, 
          py: 2.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          gap: 1.5
        }}
      >
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            px: 2.5,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || formData.emailAddresses.length === 0}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px',
            px: 3,
            backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
            }
          }}
        >
          {isSubmitting ? 'Saving...' : (isEditing ? 'Update Group' : 'Create Group')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
